(() => {
  const SELECTOR_FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const formatWithDelimiters = (cents, precision = 2, thousands = ',', decimal = '.') => {
    const amount = Number(cents) || 0;
    const fixed = (amount / 100).toFixed(precision);
    const [integer, fraction] = fixed.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return precision ? `${grouped}${decimal}${fraction}` : grouped;
  };
  const money = cents => {
    const format = window.NubeTheme?.moneyFormat || '₡ {{amount_no_decimals_with_comma_separator}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(cents, 0, '.', ',');
    const values = {
      amount: formatWithDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
      amount_with_space_separator: formatWithDelimiters(cents, 2, ' ', ','),
      amount_no_decimals_with_space_separator: formatWithDelimiters(cents, 0, ' ', ','),
      amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.')
    };
    return format.replace(match[0], values[match[1]] ?? values.amount);
  };
  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]);
  const imagePreloadCache = new Map();
  const cleanupRegistry = new WeakMap();
  let revealObserver;
  let cartMutationQueue = Promise.resolve();

  function registerCleanup(element, callback) {
    if (!element || typeof callback !== 'function') return;
    const callbacks = cleanupRegistry.get(element) || [];
    callbacks.push(callback);
    cleanupRegistry.set(element, callbacks);
  }

  function cleanupScope(scope) {
    if (!scope) return;
    [scope, ...scope.querySelectorAll('*')].forEach(element => {
      const callbacks = cleanupRegistry.get(element);
      if (!callbacks) return;
      callbacks.forEach(callback => { try { callback(); } catch (_) {} });
      cleanupRegistry.delete(element);
    });
  }

  function announce(message) {
    const region = document.getElementById('screenreader-announce');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = message; });
  }

  function largestImageSource(image) {
    if (!image) return '';
    const sourceSets = [
      ...[...(image.closest('picture')?.querySelectorAll('source[srcset]') || [])].map(source => source.srcset),
      image.srcset
    ].filter(Boolean);
    let largest = { source: image.currentSrc || image.src || '', width: 0 };
    sourceSets.forEach(sourceSet => sourceSet.split(',').forEach(candidate => {
      const match = candidate.trim().match(/^(.*?)(?:\s+(\d+(?:\.\d+)?)(w|x))?$/);
      if (!match?.[1]) return;
      const value = Number(match[2] || 1);
      const score = match[3] === 'w' ? value : value * (image.naturalWidth || 1);
      if (score >= largest.width) largest = { source: match[1].trim(), width: score };
    }));
    return largest.source;
  }

  function highResolutionImageSource(image, requestedWidth = 4096) {
    const source = largestImageSource(image);
    if (!source) return '';
    try {
      const url = new URL(source, document.baseURI);
      url.searchParams.set('width', String(Math.min(5760, Math.max(1600, Math.round(requestedWidth)))));
      return url.href;
    } catch (_) {
      return source;
    }
  }

  function preloadImage(source) {
    if (!source) return Promise.resolve(null);
    if (imagePreloadCache.has(source)) return imagePreloadCache.get(source);
    const promise = new Promise(resolve => {
      const candidate = new Image();
      candidate.decoding = 'async';
      const done = async () => {
        candidate.removeEventListener('load', done);
        candidate.removeEventListener('error', done);
        if (candidate.naturalWidth && typeof candidate.decode === 'function') {
          try { await candidate.decode(); } catch (_) {}
        }
        resolve(candidate.naturalWidth ? candidate : null);
      };
      candidate.addEventListener('load', done, { once: true });
      candidate.addEventListener('error', done, { once: true });
      candidate.src = source;
      if (candidate.complete) done();
    });
    imagePreloadCache.set(source, promise);
    return promise;
  }

  function bindOnce(element, name, callback) {
    const key = `nubeBound${name}`;
    if (!element || element.dataset[key]) return;
    element.dataset[key] = 'true';
    callback(element);
  }

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const focusable = [...container.querySelectorAll(SELECTOR_FOCUSABLE)].filter(el => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function drawers() {
    document.querySelectorAll('[data-nube-drawer]').forEach(drawer => bindOnce(drawer, 'Drawer', () => {
      let opener, closeTimer, focusTimer;
      const name = drawer.dataset.nubeDrawer;
      const panel = drawer.querySelector('[data-drawer-panel]');
      const open = trigger => {
        clearTimeout(closeTimer); clearTimeout(focusTimer);
        opener = trigger;
        drawer.hidden = false;
        requestAnimationFrame(() => drawer.classList.add('is-open'));
        document.body.classList.add('nube-drawer-open');
        trigger?.setAttribute('aria-expanded', 'true');
        focusTimer = setTimeout(() => { if (!drawer.hidden && drawer.classList.contains('is-open')) panel.querySelector(SELECTOR_FOCUSABLE)?.focus({ preventScroll: true }); }, 330);
      };
      const close = () => {
        clearTimeout(focusTimer); clearTimeout(closeTimer);
        drawer.classList.remove('is-open');
        document.body.classList.remove('nube-drawer-open');
        opener?.setAttribute('aria-expanded', 'false');
        closeTimer = setTimeout(() => { drawer.hidden = true; opener?.focus({ preventScroll: true }); }, 330);
      };
      drawer.querySelectorAll('[data-nube-close]').forEach(trigger => trigger.addEventListener('click', close));
      drawer.addEventListener('click', event => { if (event.target === drawer || event.target.hasAttribute('data-drawer-overlay')) close(); });
      drawer.addEventListener('keydown', event => { if (event.key === 'Escape') close(); trapFocus(panel, event); });
      drawer.nubeOpen = open;
      drawer.nubeClose = close;
    }));
    bindOnce(document.documentElement, 'DrawerDelegation', () => document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-nube-open]');
      if (!trigger) return;
      const drawer = document.querySelector(`[data-nube-drawer="${CSS.escape(trigger.dataset.nubeOpen)}"]`);
      drawer?.nubeOpen?.(trigger);
    }));
  }

  function header() {
    const update = () => { const hero = document.querySelector('[data-nube-hero]'); document.body.classList.toggle('nube-is-scrolled', hero ? hero.getBoundingClientRect().bottom <= 56 : true); };
    update();
    bindOnce(document.body, 'HeaderScroll', () => window.addEventListener('scroll', update, { passive: true }));
    const rail = document.querySelector('[data-nube-rail]');
    const footer = document.querySelector('[data-nube-footer]');
    if (rail && footer && !rail.dataset.nubeFooterObserver) {
      rail.dataset.nubeFooterObserver = 'true';
      const footerObserver = new IntersectionObserver(([entry]) => rail.classList.toggle('is-before-footer', entry.isIntersecting), { threshold: 0.01 });
      footerObserver.observe(footer);
      registerCleanup(rail, () => footerObserver.disconnect());
    }
  }

  function scrollExit() {
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;

    const update = () => {
      frame = 0;
      const mobile = innerWidth < 768;

      document.querySelectorAll('[data-nube-scroll-exit]').forEach(section => {
        if (reducedMotion.matches || (mobile && section.dataset.nubeScrollExitMobile !== 'true')) {
          section.style.setProperty('--nube-exit-copy-opacity', '1');
          section.style.setProperty('--nube-exit-media-opacity', '1');
          section.style.setProperty('--nube-exit-shift', '0px');
          section.style.setProperty('--nube-exit-scale', '1');
          return;
        }

        const rect = section.getBoundingClientRect();
        const type = section.dataset.nubeScrollExit;
        const sectionHeight = Math.max(1, rect.height);
        const startRatio = type === 'hero'
          ? (mobile ? 0.65 : 0.52)
          : (mobile ? 0.74 : 0.62);
        const endRatio = mobile ? 0.99 : 0.98;
        const start = sectionHeight * startRatio;
        const end = sectionHeight * endRatio;
        const progress = Math.min(1, Math.max(0, (-rect.top - start) / (end - start)));

        const strength = Math.min(1, Math.max(0.2, Number(section.dataset.nubeScrollExitStrength) || 1));
        const applied = progress * strength;
        const mediaFade = type === 'hero' ? 1 : 0.78;
        section.style.setProperty('--nube-exit-copy-opacity', (1 - applied).toFixed(4));
        section.style.setProperty('--nube-exit-media-opacity', (1 - applied * mediaFade).toFixed(4));
        section.style.setProperty('--nube-exit-shift', `${(applied * (mobile ? -6 : -10)).toFixed(2)}px`);
        section.style.setProperty('--nube-exit-scale', (1 - applied * 0.012).toFixed(4));
      });
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    bindOnce(document.documentElement, 'ScrollExit', () => {
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      reducedMotion.addEventListener?.('change', schedule);
    });
  }

  function reveals(scope = document) {
    const items = scope.querySelectorAll('[data-nube-reveal]:not([data-reveal-bound])');
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { items.forEach(item => item.classList.add('is-visible')); return; }
    const revealVisibleItems = () => document.querySelectorAll('[data-nube-reveal].is-ready:not(.is-visible)').forEach(item => {
      const rect = item.getBoundingClientRect();
      if (rect.top < innerHeight * 1.15 && rect.bottom > -innerHeight * 0.15) item.classList.add('is-visible');
    });
    revealObserver ||= new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
    }), { threshold: 0.01, rootMargin: '12% 0px 8% 0px' });
    items.forEach(item => {
      item.dataset.revealBound = 'true';
      item.classList.add('nube-reveal', 'is-ready');
      revealObserver.observe(item);
      registerCleanup(item, () => revealObserver?.unobserve(item));
    });

    // Fast scrollbar drags, restored positions and anchor jumps can skip an
    // observer delivery. Never leave an on-screen chapter invisible.
    requestAnimationFrame(revealVisibleItems);
    bindOnce(document.documentElement, 'RevealFallback', () => {
      let frame = 0;
      const schedule = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => { frame = 0; revealVisibleItems(); });
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('pageshow', schedule);
    });
  }

  function anchorNavigation(scope = document) {
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const findTarget = hash => {
      if (!hash || hash === '#') return null;
      try { return document.getElementById(decodeURIComponent(hash.slice(1))); }
      catch (_) { return null; }
    };
    const revealTarget = target => {
      if (!target) return;
      target.closest('[data-nube-reveal]')?.classList.add('is-visible');
      target.querySelectorAll?.('[data-nube-reveal]').forEach(item => item.classList.add('is-visible'));
    };
    const align = (target, behavior = 'smooth') => {
      if (!target) return;
      revealTarget(target);
      const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const top = Math.max(0, target.getBoundingClientRect().top + scrollY - offset);
      window.scrollTo({ top, left: 0, behavior: reducedMotion ? 'auto' : behavior });
    };

    scope.querySelectorAll('a[href*="#"]').forEach(link => bindOnce(link, 'AnchorScroll', () => link.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
      const destination = new URL(link.href, location.href);
      if (destination.origin !== location.origin || destination.pathname !== location.pathname || destination.search !== location.search) return;
      const target = findTarget(destination.hash);
      if (!target) return;
      event.preventDefault();
      history.pushState(null, '', destination.hash);
      align(target);
    })));

    if (scope !== document || !location.hash || document.documentElement.dataset.nubeHashAligned) return;
    document.documentElement.dataset.nubeHashAligned = 'true';
    const realign = () => align(findTarget(location.hash), 'auto');
    requestAnimationFrame(realign);
    if (document.readyState === 'complete') realign();
    else window.addEventListener('load', realign, { once: true });
    if (document.fonts?.ready) document.fonts.ready.then(realign).catch(() => {});
    window.addEventListener('pageshow', realign, { once: true });
    bindOnce(document.documentElement, 'HashAlignment', () => window.addEventListener('hashchange', () => {
      const target = findTarget(location.hash);
      if (target) align(target, 'auto');
    }));
  }

  function materials(scope = document) {
    scope.querySelectorAll('[data-nube-materials]').forEach(component => bindOnce(component, 'Materials', () => {
      const image = component.querySelector('[data-material-image]');
      const rows = [...component.querySelectorAll('[data-material-row]')];
      let activation = 0;
      const activate = async row => {
        const token = ++activation;
        rows.forEach(item => { item.classList.toggle('is-active', item === row); item.setAttribute('aria-current', item === row ? 'true' : 'false'); });
        if (!image) return;
        const src = row.dataset.image;
        if (src && image.getAttribute('src') !== src) {
          const loaded = await preloadImage(src);
          if (!loaded || token !== activation || !image.isConnected) return;
          image.classList.add('is-changing');
          setTimeout(() => { if (token !== activation) return; image.src = src; image.srcset = row.dataset.srcset || ''; image.alt = row.dataset.alt || ''; image.classList.remove('is-changing'); }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 120);
        }
      };
      rows.forEach(row => {
        row.addEventListener('mouseenter', () => activate(row));
        row.addEventListener('focus', () => activate(row));
        row.addEventListener('pointerdown', () => activate(row));
      });
    }));
  }

  function quantity(scope = document) {
    scope.querySelectorAll('[data-nube-quantity]').forEach(control => bindOnce(control, 'Quantity', () => {
      const input = control.querySelector('input');
      control.querySelector('[data-quantity-minus]')?.addEventListener('click', () => { input.value = Math.max(Number(input.min) || 1, Number(input.value) - (Number(input.step) || 1)); input.dispatchEvent(new Event('change', { bubbles: true })); });
      control.querySelector('[data-quantity-plus]')?.addEventListener('click', () => { input.value = Math.min(input.max ? Number(input.max) : Infinity, Math.max(Number(input.min) || 1, Number(input.value) + (Number(input.step) || 1))); input.dispatchEvent(new Event('change', { bubbles: true })); });
    }));
  }

  function accordions(scope = document) {
    scope.querySelectorAll('.nube-accordion').forEach(details => bindOnce(details, 'Accordion', () => {
      let closeTimer;
      if (details.open) details.classList.add('is-open');
      details.querySelector('summary')?.addEventListener('click', event => {
        event.preventDefault();
        clearTimeout(closeTimer);
        if (details.classList.contains('is-open')) {
          details.classList.remove('is-open');
          closeTimer = setTimeout(() => { details.open = false; }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 225);
        } else {
          details.open = true;
          requestAnimationFrame(() => details.classList.add('is-open'));
        }
      });
    }));
  }

  function productForms(scope = document) {
    scope.querySelectorAll('[data-nube-product]').forEach(component => bindOnce(component, 'Product', () => {
      const json = component.querySelector('[data-product-json]');
      if (!json) return;
      const product = JSON.parse(json.textContent);
      const form = component.querySelector('[data-nube-product-form]');
      const idInput = form?.querySelector('[name="id"]');
      const button = form?.querySelector('[type="submit"]');
      const prices = [...component.querySelectorAll('[data-nube-price]')];
      const gallery = component.querySelector('[data-nube-gallery]');
      const galleryItems = [...(gallery?.querySelectorAll('[data-media-id]') || [])];
      const sticky = component.querySelector('[data-nube-sticky-buy]');
      const quantityInput = form?.querySelector('[name="quantity"]');
      const options = [...component.querySelectorAll('[data-option-value]')];
      const optionGroups = [...component.querySelectorAll('[data-option-group]')];
      const mobileZoomEnabled = component.dataset.nubeMobileZoom === 'true';
      const mobilePreviewWidth = () => Math.min(2000, Math.max(1600, Math.ceil(innerWidth * (devicePixelRatio || 1) * 1.35)));
      const preloadMobileItems = items => {
        if (!matchMedia('(max-width: 767px)').matches || navigator.connection?.saveData) return;
        items.slice(0, 2).forEach(item => {
          const image = item.querySelector('img');
          if (image) preloadImage(highResolutionImageSource(image, mobilePreviewWidth()));
        });
      };
      const selectedValues = () => optionGroups.map(group => group.querySelector('[data-option-value]:checked')?.value);
      const currentVariant = () => {
        if (!optionGroups.length) return product.variants.find(variant => String(variant.id) === String(idInput?.value)) || product.variants[0];
        return product.variants.find(variant => variant.options.every((value, index) => value === selectedValues()[index]));
      };
      let mediaChangeToken = 0;
      const prepareVariantImage = image => {
        if (!image) return Promise.resolve();
        image.loading = 'eager';
        if (image.complete && image.naturalWidth > 0) {
          return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
        }
        return new Promise(resolve => {
          const done = () => {
            image.removeEventListener('load', done);
            image.removeEventListener('error', done);
            resolve();
          };
          image.addEventListener('load', done, { once: true });
          image.addEventListener('error', done, { once: true });
          if (image.complete) done();
        });
      };
      const preloadVariant = input => {
        if (input.disabled) return;
        const group = input.closest('[data-option-group]');
        if (!group) return;
        const candidateValues = selectedValues();
        candidateValues[Number(group.dataset.optionIndex)] = input.value;
        const matchingVariant = product.variants.find(variant => variant.available && variant.options.every((value, index) => value === candidateValues[index]))
          || product.variants.find(variant => variant.options.every((value, index) => value === candidateValues[index]));
        const featuredMedia = matchingVariant?.featured_media || matchingVariant?.featured_image;
        if (!featuredMedia) return;
        const matchingItem = galleryItems.find(item => item.dataset.mediaGroupId === String(featuredMedia.id || ''));
        if (matchingItem) {
          const variantItems = galleryItems.filter(item => item.dataset.mediaGroupId === String(featuredMedia.id || '') || item.dataset.mediaShared === 'true');
          prepareVariantImage(matchingItem.querySelector('img'));
          preloadMobileItems(variantItems.length ? variantItems : [matchingItem]);
        }
        else if (featuredMedia.src) {
          const preload = new Image();
          preload.src = featuredMedia.src;
        }
      };
      const updateVariantMedia = async (variant, shouldMove = false) => {
        const changeToken = ++mediaChangeToken;
        const featuredMedia = variant?.featured_media || variant?.featured_image;
        if (!gallery) return;
        if (!featuredMedia) {
          galleryItems.forEach(item => { item.hidden = false; item.classList.remove('is-lead'); });
          galleryItems[0]?.classList.add('is-lead');
          return;
        }
        const groupId = String(featuredMedia.id || '');
        const filterMedia = gallery.dataset.variantMedia === 'true' || gallery.dataset.singleVariant === 'true';
        const groupedItems = groupId ? galleryItems.filter(item => item.dataset.mediaGroupId === groupId) : [];
        const sharedItems = galleryItems.filter(item => item.dataset.mediaShared === 'true');
        const matchingItems = [...groupedItems, ...sharedItems];

        if (filterMedia && matchingItems.length) {
          await prepareVariantImage(matchingItems[0]?.querySelector('img'));
          if (changeToken !== mediaChangeToken) return;
          const keepFeatured = gallery.dataset.alwaysFeatured === 'true';
          galleryItems.forEach(item => { item.hidden = !(matchingItems.includes(item) || (keepFeatured && item.dataset.productFeatured === 'true')); });
          preloadMobileItems(matchingItems);
          galleryItems.forEach(item => item.classList.remove('is-lead'));
          const lead = keepFeatured ? galleryItems.find(item => item.dataset.productFeatured === 'true') || matchingItems[0] : matchingItems[0];
          lead?.classList.add('is-lead');
          if (shouldMove) {
            const behavior = gallery.dataset.mediaBehavior === 'scroll' && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
            if (innerWidth >= 768 && gallery.dataset.mediaBehavior === 'scroll') {
              matchingItems[0]?.scrollIntoView({ behavior, block: 'start' });
            }
          }
          return;
        }

        const target = galleryItems.find(item => item.dataset.mediaId === groupId) || galleryItems[0];
        await prepareVariantImage(target?.querySelector('img'));
        if (changeToken !== mediaChangeToken) return;
        galleryItems.forEach(item => { item.hidden = false; item.classList.remove('is-lead'); gallery.appendChild(item); });
        const keepFeatured = gallery.dataset.alwaysFeatured === 'true';
        const swap = gallery.dataset.mediaBehavior !== 'scroll' || innerWidth < 1024;
        if (swap && !keepFeatured && target) gallery.prepend(target);
        const first = keepFeatured ? galleryItems.find(item => item.dataset.productFeatured === 'true') : swap ? target : galleryItems[0];
        first?.classList.add('is-lead');
        if (shouldMove && !swap && target) target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      };
      const updateAvailability = () => options.forEach(input => {
        const index = Number(input.closest('[data-option-group]')?.dataset.optionIndex);
        const selected = selectedValues();
        const matches = product.variants.filter(variant => variant.options[index] === input.value
          && variant.options.every((value, i) => i >= index || selected[i] == null || selected[i] === value));
        const exists = matches.length > 0;
        const available = matches.some(variant => variant.available);
        input.disabled = !exists || !available;
        input.setAttribute('aria-disabled', String(!exists || !available));
        input.closest('label')?.classList.toggle('is-unavailable', !exists);
        input.closest('label')?.classList.toggle('is-sold-out', exists && !available);
      });
      const reconcileOptions = input => {
        if (!input?.matches?.('[data-option-value]')) return;
        const index = Number(input.closest('[data-option-group]').dataset.optionIndex);
        const selected = selectedValues();
        const candidates = product.variants.filter(variant => variant.options.every((value, i) => i > index || value === selected[i]));
        const exact = candidates.find(variant => variant.options.every((value, i) => value === selected[i]));
        const next = exact?.available ? exact : candidates.find(variant => variant.available) || exact || candidates[0];
        if (next) optionGroups.forEach((group, i) => {
          if (i <= index) return;
          group.querySelectorAll('[data-option-value]').forEach(option => { option.checked = option.value === next.options[i]; });
        });
      };
      const update = event => {
        reconcileOptions(event?.target);
        const variant = currentVariant();
        updateAvailability();
        optionGroups.forEach(group => { const label = group.querySelector('[data-option-current]'); if (label) label.textContent = group.querySelector('[data-option-value]:checked')?.value || ''; });
        if (!variant) {
          idInput.value = '';
          button.disabled = true;
          button.textContent = window.NubeTheme?.strings?.unavailable || 'NO DISPONIBLE';
          if (sticky) { sticky.disabled = true; sticky.querySelector('span:first-child').textContent = button.textContent; sticky.querySelector('[data-sticky-price]').textContent = ''; }
          return;
        }
        idInput.value = variant.id;
        if (quantityInput) {
          const rule = variant.quantity_rule || {};
          quantityInput.min = rule.min || 1;
          quantityInput.step = rule.increment || 1;
          if (rule.max != null) quantityInput.max = rule.max; else quantityInput.removeAttribute('max');
          const min = Number(quantityInput.min), step = Number(quantityInput.step);
          let value = Math.max(min, Number(quantityInput.value) || min);
          value = min + Math.floor((value - min) / step) * step;
          if (quantityInput.max) value = Math.min(Number(quantityInput.max), value);
          quantityInput.value = value;
        }
        prices.forEach(price => price.textContent = variant.formatted_price);
        button.disabled = !variant.available || form.dataset.submitting === 'true';
        const total = (variant.price || 0) * Math.max(1, Number(quantityInput?.value || 1));
        const totalLabel = money(total, window.Shopify?.currency?.active || 'CRC');
        button.textContent = variant.available ? `${button.dataset.availableText} · ${totalLabel}` : button.dataset.soldoutText;
        if (sticky) { sticky.disabled = button.disabled; sticky.querySelector('span:first-child').textContent = variant.available ? button.dataset.availableText : button.dataset.soldoutText; sticky.querySelector('[data-sticky-price]').textContent = variant.available ? totalLabel : ''; }
        if (!event || event.target?.matches?.('[data-option-value]')) updateVariantMedia(variant, Boolean(event));
        if (history.replaceState) {
          const nextUrl = new URL(location.href);
          nextUrl.searchParams.set('variant', variant.id);
          history.replaceState({}, '', nextUrl);
        }
        component.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      };
      options.forEach(input => {
        const interactionTarget = input.closest('label') || input;
        input.addEventListener('change', update);
        interactionTarget.addEventListener('pointerenter', () => preloadVariant(input));
        input.addEventListener('focus', () => preloadVariant(input));
        interactionTarget.addEventListener('touchstart', () => preloadVariant(input), { passive: true });
      });
      quantityInput?.addEventListener('change', update);
      form.nubeRefresh = () => update({ target: quantityInput });
      update();
      if (matchMedia('(max-width: 767px)').matches && !navigator.connection?.saveData) {
        const variantLeadItems = galleryItems.filter((item, itemIndex, items) => {
          const groupId = item.dataset.mediaGroupId;
          return groupId && items.findIndex(candidate => candidate.dataset.mediaGroupId === groupId) === itemIndex;
        });
        const preloadVariantLeads = () => variantLeadItems.forEach((item, itemIndex) => {
          const image = item.querySelector('img');
          if (!image) return;
          setTimeout(() => preloadImage(highResolutionImageSource(image, mobilePreviewWidth())), itemIndex * 140);
        });
        if ('requestIdleCallback' in window) requestIdleCallback(preloadVariantLeads, { timeout: mobileZoomEnabled ? 1800 : 2600 });
        else setTimeout(preloadVariantLeads, mobileZoomEnabled ? 600 : 1200);
      }
      if (sticky && button) {
        const stop = component.querySelector('[data-nube-sticky-buy-stop]') || component;
        let frame = 0;
        const observe = () => {
          frame = 0;
          const viewport = window.visualViewport;
          const viewportTop = viewport?.offsetTop || 0;
          const viewportBottom = viewportTop + (viewport?.height || innerHeight);
          const visibleGalleryItems = [...component.querySelectorAll('.nube-product-gallery__item:not([hidden])')];
          const lastGalleryItem = visibleGalleryItems[visibleGalleryItems.length - 1];
          const boundary = lastGalleryItem?.getBoundingClientRect() || stop.getBoundingClientRect();
          const stickyHeight = sticky.getBoundingClientRect().height || 64;
          const mediaClearance = stickyHeight + 16;
          const bottom = lastGalleryItem ? boundary.bottom : (stop === component ? boundary.bottom : boundary.top);
          const active = innerWidth < 768 && button.getBoundingClientRect().bottom <= viewportTop
            && bottom > viewportBottom - mediaClearance;
          sticky.classList.toggle('is-active', active);
          sticky.setAttribute('aria-hidden', String(!active));
          sticky.tabIndex = active ? 0 : -1;
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(observe); };
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        window.visualViewport?.addEventListener('resize', schedule);
        window.visualViewport?.addEventListener('scroll', schedule);
        const resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(component);
        resizeObserver.observe(button);
        component.addEventListener('variant:change', schedule);
        component.addEventListener('load', schedule, true);
        sticky.addEventListener('click', () => { if (!button.disabled) form.requestSubmit(button); });
        const cleanup = event => {
          if (!event.target.contains(component)) return;
          window.removeEventListener('scroll', schedule);
          window.removeEventListener('resize', schedule);
          window.visualViewport?.removeEventListener('resize', schedule);
          window.visualViewport?.removeEventListener('scroll', schedule);
          resizeObserver.disconnect();
          cancelAnimationFrame(frame);
          document.removeEventListener('shopify:section:unload', cleanup);
        };
        document.addEventListener('shopify:section:unload', cleanup);
        observe();
      }
    }));
  }

  function productMagnifier(scope = document) {
    const finePointer = matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
    if (!finePointer.matches) return;

    scope.querySelectorAll('[data-nube-product][data-nube-magnifier="true"] .nube-product-gallery__item').forEach(item => bindOnce(item, 'Magnifier', () => {
      const image = item.querySelector('img');
      if (!image) return;

      const component = item.closest('[data-nube-product]');
      const lens = document.createElement('span');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', {
        alpha: true,
        desynchronized: true
      });
      const lensSize = Math.min(240, Math.max(140, Number(component?.dataset.nubeMagnifierSize) || 184));
      const zoom = Math.min(3, Math.max(1.5, Number(component?.dataset.nubeMagnifierZoom) || 2.2));
      const distortion = Math.min(0.1, Math.max(0, Number(component?.dataset.nubeMagnifierDistortion) / 100 || 0));
      const processorCount = navigator.hardwareConcurrency || 4;
      const memory = navigator.deviceMemory || 4;
      const rings = processorCount <= 4 || memory <= 4 ? 14 : processorCount >= 10 && memory >= 8 ? 22 : 18;
      let pointer = null;
      let frame = 0;
      let detailImage = null;
      let detailSource = '';
      let detailReady = false;
      let detailPromise = null;
      let detailToken = 0;

      if (!context) return;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      lens.className = 'nube-product-magnifier';
      lens.setAttribute('aria-hidden', 'true');
      const canvasPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(lensSize * canvasPixelRatio);
      canvas.height = canvas.width;
      lens.appendChild(canvas);
      item.appendChild(lens);

      const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
      const getDisplayedSource = () => image.currentSrc || image.src;
      const getDetailSource = source => {
        try {
          const url = new URL(source, document.baseURI);
          url.searchParams.set('width', '2400');
          return url.href;
        } catch (_) {
          return source;
        }
      };
      const prepareDetailImage = () => {
        const displayedSource = getDisplayedSource();
        if (detailSource === displayedSource && detailReady && detailImage) return Promise.resolve(true);
        if (detailSource === displayedSource && detailPromise) return detailPromise;

        const token = ++detailToken;
        const candidate = new Image();
        candidate.decoding = 'async';
        detailSource = displayedSource;
        detailImage = candidate;
        detailReady = false;
        candidate.src = getDetailSource(displayedSource);

        detailPromise = new Promise(resolve => {
          const done = async () => {
            candidate.removeEventListener('load', done);
            candidate.removeEventListener('error', done);
            if (candidate.naturalWidth && typeof candidate.decode === 'function') {
              try { await candidate.decode(); } catch (_) {}
            }
            const ready = token === detailToken && candidate.naturalWidth > 0;
            if (token === detailToken) {
              detailReady = ready;
              detailPromise = null;
            }
            resolve(ready);
          };
          candidate.addEventListener('load', done, { once: true });
          candidate.addEventListener('error', done, { once: true });
          if (candidate.complete) done();
        });
        return detailPromise;
      };
      const hide = () => {
        pointer = null;
        lens.classList.remove('is-active');
        item.classList.remove('is-magnifying');
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
      };

      const draw = () => {
        frame = 0;
        if (!pointer || !detailReady || !detailImage?.naturalWidth) return;

        const imageRect = image.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        if (!imageRect.width || !imageRect.height) return;

        const naturalWidth = detailImage.naturalWidth;
        const naturalHeight = detailImage.naturalHeight;
        const objectFit = getComputedStyle(image).objectFit;
        const scale = objectFit === 'contain'
          ? Math.min(imageRect.width / naturalWidth, imageRect.height / naturalHeight)
          : Math.max(imageRect.width / naturalWidth, imageRect.height / naturalHeight);
        const renderedWidth = naturalWidth * scale;
        const renderedHeight = naturalHeight * scale;
        const offsetX = (imageRect.width - renderedWidth) / 2;
        const offsetY = (imageRect.height - renderedHeight) / 2;
        const sourceX = (pointer.x - imageRect.left - offsetX) / scale;
        const sourceY = (pointer.y - imageRect.top - offsetY) / scale;
        const lensLeft = pointer.x - itemRect.left - lensSize / 2;
        const lensTop = pointer.y - itemRect.top - lensSize / 2;
        const pixelRatio = canvas.width / lensSize;

        lens.style.transform = `translate3d(${lensLeft}px, ${lensTop}px, 0)`;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.clearRect(0, 0, lensSize, lensSize);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        try {
          for (let ring = rings; ring >= 1; ring -= 1) {
            const progress = ring / rings;
            const radius = (lensSize / 2) * progress;
            const ringZoom = zoom * (1 + distortion * 0.36 - distortion * 0.9 * progress * progress);
            const sourceWidth = Math.min(naturalWidth, lensSize / (ringZoom * scale));
            const sourceHeight = Math.min(naturalHeight, lensSize / (ringZoom * scale));
            const requestedLeft = sourceX - sourceWidth / 2;
            const requestedTop = sourceY - sourceHeight / 2;
            const sourceLeft = clamp(requestedLeft, 0, naturalWidth);
            const sourceTop = clamp(requestedTop, 0, naturalHeight);
            const sourceRight = clamp(requestedLeft + sourceWidth, 0, naturalWidth);
            const sourceBottom = clamp(requestedTop + sourceHeight, 0, naturalHeight);
            const clippedWidth = sourceRight - sourceLeft;
            const clippedHeight = sourceBottom - sourceTop;
            if (clippedWidth <= 0 || clippedHeight <= 0) continue;
            const destinationLeft = ((sourceLeft - requestedLeft) / sourceWidth) * lensSize;
            const destinationTop = ((sourceTop - requestedTop) / sourceHeight) * lensSize;
            const destinationWidth = (clippedWidth / sourceWidth) * lensSize;
            const destinationHeight = (clippedHeight / sourceHeight) * lensSize;

            context.save();
            context.beginPath();
            context.arc(lensSize / 2, lensSize / 2, radius, 0, Math.PI * 2);
            context.clip();
            context.drawImage(detailImage, sourceLeft, sourceTop, clippedWidth, clippedHeight, destinationLeft, destinationTop, destinationWidth, destinationHeight);
            context.restore();
          }
        } catch (_) {
          hide();
        }
      };

      const schedule = event => {
        pointer = { x: event.clientX, y: event.clientY };
        if (detailReady) {
          lens.classList.add('is-active');
          item.classList.add('is-magnifying');
          if (!frame) frame = requestAnimationFrame(draw);
          return;
        }
        prepareDetailImage().then(ready => {
          if (!ready || !pointer) return;
          lens.classList.add('is-active');
          item.classList.add('is-magnifying');
          if (!frame) frame = requestAnimationFrame(draw);
        });
      };

      item.addEventListener('pointerenter', schedule);
      item.addEventListener('pointermove', schedule);
      item.addEventListener('pointerleave', hide);
      item.addEventListener('pointercancel', hide);
      window.addEventListener('scroll', hide, { passive: true });
      registerCleanup(item, () => {
        window.removeEventListener('scroll', hide);
        if (frame) cancelAnimationFrame(frame);
        lens.remove();
      });
    }));
  }

  function mobileProductViewer(scope = document) {
    scope.querySelectorAll('[data-nube-product][data-nube-mobile-zoom="true"]').forEach(component => bindOnce(component, 'MobileViewer', () => {
      const gallery = component.querySelector('[data-nube-gallery]');
      if (!gallery) return;

      const galleryItems = [...gallery.querySelectorAll('[data-media-id]')];
      const maximumScale = Math.min(4, Math.max(2, Number(component.dataset.nubeMobileZoomMax) || 3));
      const viewer = document.createElement('div');
      viewer.className = 'nube-mobile-viewer';
      viewer.hidden = true;
      viewer.setAttribute('role', 'dialog');
      viewer.setAttribute('aria-modal', 'true');
      viewer.setAttribute('aria-label', 'Visor de imágenes del producto');
      viewer.innerHTML = `<header class="nube-mobile-viewer__header"><span data-mobile-viewer-count>01 / 01</span><button type="button" data-mobile-viewer-close aria-label="Cerrar visor">CERRAR ×</button></header><div class="nube-mobile-viewer__stage" data-mobile-viewer-stage><img draggable="false" alt=""></div><footer class="nube-mobile-viewer__footer"><button type="button" data-mobile-viewer-prev aria-label="Imagen anterior">← ANTERIOR</button><span>TOCA DOS VECES O PELLIZCA PARA AMPLIAR</span><button type="button" data-mobile-viewer-next aria-label="Imagen siguiente">SIGUIENTE →</button></footer>`;
      document.body.appendChild(viewer);
      registerCleanup(component, () => {
        document.body.classList.remove('nube-viewer-open');
        viewer.remove();
      });

      const stage = viewer.querySelector('[data-mobile-viewer-stage]');
      const viewerImage = stage.querySelector('img');
      const counter = viewer.querySelector('[data-mobile-viewer-count]');
      const closeButton = viewer.querySelector('[data-mobile-viewer-close]');
      const previousButton = viewer.querySelector('[data-mobile-viewer-prev]');
      const nextButton = viewer.querySelector('[data-mobile-viewer-next]');
      const pointers = new Map();
      let visibleItems = [];
      let index = 0;
      let scale = 1;
      let translateX = 0;
      let translateY = 0;
      let gestureStart = null;
      let lastTap = null;
      let previousFocus = null;
      let loadToken = 0;

      const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
      const detailWidth = () => Math.min(5760, Math.max(4096, Math.ceil(stage.clientWidth * (devicePixelRatio || 1) * maximumScale)));
      const applyTransform = animate => {
        viewerImage.classList.toggle('is-animating', Boolean(animate));
        viewerImage.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
      };
      const constrainTranslation = () => {
        if (scale <= 1) {
          scale = 1;
          translateX = 0;
          translateY = 0;
          return;
        }
        const maximumX = Math.max(0, (viewerImage.clientWidth * scale - stage.clientWidth) / 2);
        const maximumY = Math.max(0, (viewerImage.clientHeight * scale - stage.clientHeight) / 2);
        translateX = clamp(translateX, -maximumX, maximumX);
        translateY = clamp(translateY, -maximumY, maximumY);
      };
      const resetZoom = animate => {
        scale = 1;
        translateX = 0;
        translateY = 0;
        applyTransform(animate);
      };
      const showImage = async nextIndex => {
        if (!visibleItems.length) return;
        index = (nextIndex + visibleItems.length) % visibleItems.length;
        const sourceImage = visibleItems[index].querySelector('img');
        if (!sourceImage) return;
        const token = ++loadToken;
        resetZoom(false);
        counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(visibleItems.length).padStart(2, '0')}`;
        previousButton.hidden = visibleItems.length < 2;
        nextButton.hidden = visibleItems.length < 2;
        const source = highResolutionImageSource(sourceImage, detailWidth());
        const prepared = await preloadImage(source);
        if (token !== loadToken || !prepared) return;
        viewerImage.classList.remove('is-ready');
        viewerImage.alt = sourceImage.alt || '';
        viewerImage.src = source;
        if (typeof viewerImage.decode === 'function') {
          try { await viewerImage.decode(); } catch (_) {}
        }
        if (token === loadToken) viewerImage.classList.add('is-ready');
      };
      const open = item => {
        if (innerWidth >= 768) return;
        visibleItems = galleryItems.filter(candidate => !candidate.hidden);
        visibleItems.forEach(item => {
          const image = item.querySelector('img');
          if (image) preloadImage(highResolutionImageSource(image, detailWidth()));
        });
        index = Math.max(0, visibleItems.indexOf(item));
        previousFocus = document.activeElement;
        viewer.hidden = false;
        document.body.classList.add('nube-viewer-open');
        requestAnimationFrame(() => viewer.classList.add('is-open'));
        showImage(index);
        closeButton.focus({ preventScroll: true });
      };
      const close = () => {
        viewer.classList.remove('is-open');
        document.body.classList.remove('nube-viewer-open');
        pointers.clear();
        resetZoom(false);
        setTimeout(() => {
          viewer.hidden = true;
          previousFocus?.focus?.({ preventScroll: true });
        }, 240);
      };
      const distance = values => Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
      const midpoint = values => ({ x: (values[0].x + values[1].x) / 2, y: (values[0].y + values[1].y) / 2 });
      const beginGesture = () => {
        const values = [...pointers.values()];
        if (values.length === 1) {
          gestureStart = { x: values[0].x, y: values[0].y, translateX, translateY, scale, time: performance.now() };
        } else if (values.length === 2) {
          const middle = midpoint(values);
          const rect = stage.getBoundingClientRect();
          gestureStart = {
            distance: distance(values),
            scale,
            contentX: (middle.x - rect.left - rect.width / 2 - translateX) / scale,
            contentY: (middle.y - rect.top - rect.height / 2 - translateY) / scale
          };
        }
      };

      galleryItems.forEach(item => {
        const image = item.querySelector('img');
        if (!image) return;
        image.addEventListener('click', event => {
          if (innerWidth >= 768) return;
          event.preventDefault();
          open(item);
        });
      });

      stage.addEventListener('pointerdown', event => {
        stage.setPointerCapture?.(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        viewerImage.classList.remove('is-animating');
        beginGesture();
      });
      stage.addEventListener('pointermove', event => {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const values = [...pointers.values()];
        if (values.length === 2 && gestureStart?.distance) {
          const rect = stage.getBoundingClientRect();
          const middle = midpoint(values);
          scale = clamp(gestureStart.scale * distance(values) / gestureStart.distance, 1, maximumScale);
          translateX = middle.x - rect.left - rect.width / 2 - gestureStart.contentX * scale;
          translateY = middle.y - rect.top - rect.height / 2 - gestureStart.contentY * scale;
          constrainTranslation();
          applyTransform(false);
        } else if (values.length === 1 && gestureStart && scale > 1) {
          translateX = gestureStart.translateX + values[0].x - gestureStart.x;
          translateY = gestureStart.translateY + values[0].y - gestureStart.y;
          constrainTranslation();
          applyTransform(false);
        }
      });
      const finishPointer = event => {
        const ended = pointers.get(event.pointerId);
        const wasSingle = pointers.size === 1;
        pointers.delete(event.pointerId);

        if (wasSingle && ended && gestureStart) {
          const deltaX = ended.x - gestureStart.x;
          const deltaY = ended.y - gestureStart.y;
          const elapsed = performance.now() - gestureStart.time;
          if (scale === 1 && elapsed < 500 && Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY)) {
            showImage(index + (deltaX < 0 ? 1 : -1));
            lastTap = null;
          } else if (elapsed < 280 && Math.abs(deltaX) < 12 && Math.abs(deltaY) < 12) {
            const now = performance.now();
            if (lastTap && now - lastTap.time < 300 && Math.hypot(ended.x - lastTap.x, ended.y - lastTap.y) < 28) {
              const rect = stage.getBoundingClientRect();
              if (scale > 1) resetZoom(true);
              else {
                const targetScale = Math.min(2.2, maximumScale);
                translateX = -(ended.x - rect.left - rect.width / 2) * (targetScale - 1);
                translateY = -(ended.y - rect.top - rect.height / 2) * (targetScale - 1);
                scale = targetScale;
                constrainTranslation();
                applyTransform(true);
              }
              lastTap = null;
            } else lastTap = { x: ended.x, y: ended.y, time: now };
          }
        }

        if (scale < 1.02) resetZoom(true);
        else {
          constrainTranslation();
          applyTransform(true);
        }
        if (pointers.size) beginGesture();
      };
      stage.addEventListener('pointerup', finishPointer);
      stage.addEventListener('pointercancel', finishPointer);
      closeButton.addEventListener('click', close);
      previousButton.addEventListener('click', () => showImage(index - 1));
      nextButton.addEventListener('click', () => showImage(index + 1));
      viewer.addEventListener('keydown', event => {
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowLeft') showImage(index - 1);
        if (event.key === 'ArrowRight') showImage(index + 1);
        trapFocus(viewer, event);
      });
    }));
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('No se pudo actualizar el carrito.');
    return response.json();
  }

  function renderCart(cart) {
    document.querySelectorAll('[data-nube-cart-count]').forEach(node => node.textContent = cart.item_count);
    const drawer = document.querySelector('[data-nube-drawer="cart"]');
    if (!drawer) return;
    const items = drawer.querySelector('[data-cart-items]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    const checkoutTotal = drawer.querySelector('[data-cart-checkout-total]');
    subtotal.textContent = money(cart.total_price, cart.currency);
    if (checkoutTotal) checkoutTotal.textContent = money(cart.total_price, cart.currency);
    if (!cart.items.length) {
      const empty = window.NubeTheme?.strings?.cartEmpty || 'TU CARRITO ESTÁ VACÍO.';
      const view = window.NubeTheme?.strings?.viewObjects || 'VER OBJETOS →';
      const shopUrl = window.NubeTheme?.routes?.allProducts || '/collections/all';
      items.innerHTML = `<div class="nube-cart-empty"><p>${escapeHTML(empty)}</p><a href="${escapeHTML(shopUrl)}">${escapeHTML(view)}</a></div>`;
      drawer.classList.add('is-empty');
      return;
    }
    drawer.classList.remove('is-empty');
    items.innerHTML = cart.items.map((item, index) => `<article class="nube-cart-line" data-line="${index + 1}">
      ${item.image ? `<a class="nube-cart-line__image" href="${escapeHTML(item.url)}"><img src="${escapeHTML(item.image)}${item.image.includes('?') ? '&' : '?'}width=240" alt="${escapeHTML(item.product_title)}" width="120" height="150"></a>` : ''}
      <div class="nube-cart-line__copy"><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title).toUpperCase()}</a>${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHTML(item.variant_title)}</small>` : ''}${Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_')).map(([key, value]) => `<small>${escapeHTML(key)}: ${escapeHTML(value)}</small>`).join('')}
      <div class="nube-cart-line__quantity"><button type="button" data-cart-change="${escapeHTML(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="${escapeHTML(window.NubeTheme?.strings?.reduceQuantity || 'Reducir cantidad')}">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${escapeHTML(item.key)}" data-quantity="${item.quantity + 1}" aria-label="${escapeHTML(window.NubeTheme?.strings?.increaseQuantity || 'Aumentar cantidad')}">+</button><button class="nube-cart-remove" type="button" data-cart-change="${escapeHTML(item.key)}" data-quantity="0">${escapeHTML(window.NubeTheme?.strings?.remove || 'ELIMINAR')}</button></div></div><span>${money(item.final_line_price, cart.currency)}</span></article>`).join('');
  }

  async function updateCart(line, quantityValue) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line, quantity: quantityValue }) });
    if (!response.ok) throw new Error(window.NubeTheme?.strings?.cartError || 'No se pudo actualizar el carrito.');
    renderCart(await response.json());
  }

  function cart(scope = document) {
    scope.querySelectorAll('[data-nube-product-form]').forEach(form => bindOnce(form, 'AjaxCart', () => form.addEventListener('submit', async event => {
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button.disabled || form.dataset.submitting === 'true' || !form.reportValidity()) return;
      form.dataset.submitting = 'true';
      form.setAttribute('aria-busy', 'true');
      const error = form.querySelector('[data-product-error]');
      const sticky = form.closest('[data-nube-product]')?.querySelector('[data-nube-sticky-buy]');
      const stickyLabel = sticky?.querySelector('span:first-child');
      const defaultStickyLabel = sticky?.dataset.defaultLabel || stickyLabel?.textContent;
      const defaultButtonLabel = button.textContent;
      if (sticky && defaultStickyLabel) sticky.dataset.defaultLabel = defaultStickyLabel;
      let addedSuccessfully = false;
      button.disabled = true; error.textContent = '';
      if (sticky) sticky.disabled = true;
      try {
        const payload = new FormData(form);
        const operation = cartMutationQueue.then(async () => {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: payload });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'No se pudo añadir el producto.');
          return result;
        });
        cartMutationQueue = operation.catch(() => {});
        const result = await operation;
        try { renderCart(await fetchCart()); } catch (_) { error.textContent = 'Producto añadido. Abre el carrito para actualizar el total.'; }
        announce(`${result.product_title || 'Producto'} añadido al carrito.`);
        if (innerWidth < 768) {
          addedSuccessfully = true;
          window.clearTimeout(button.nubeAddedTimer);
          button.classList.add('is-added');
          button.textContent = 'AÑADIDO';
          if (sticky && stickyLabel) {
            window.clearTimeout(sticky.nubeAddedTimer);
            sticky.classList.add('is-added');
            stickyLabel.textContent = 'AÑADIDO';
          }
          button.nubeAddedTimer = window.setTimeout(() => {
            button.classList.remove('is-added');
            button.disabled = false;
            if (sticky && stickyLabel) {
              sticky.classList.remove('is-added');
              stickyLabel.textContent = defaultStickyLabel;
              sticky.disabled = false;
            }
            const quantity = form.querySelector('[name="quantity"]');
            if (quantity) quantity.dispatchEvent(new Event('change', { bubbles: true }));
            else { button.textContent = defaultButtonLabel; form.nubeRefresh?.(); }
          }, 1400);
        }
        const cartDrawer = document.querySelector('[data-nube-drawer="cart"]');
        const openAfterAdd = innerWidth < 768 ? cartDrawer?.dataset.openAfterAddMobile : cartDrawer?.dataset.openAfterAddDesktop;
        if (openAfterAdd === 'true') cartDrawer?.nubeOpen(button);
      } catch (exception) { error.textContent = exception.message; }
      finally { delete form.dataset.submitting; form.removeAttribute('aria-busy'); if (!addedSuccessfully) form.nubeRefresh?.(); }
    })));
    const drawer = document.querySelector('[data-nube-drawer="cart"]');
    bindOnce(drawer, 'CartChanges', () => drawer.addEventListener('click', event => {
      const trigger = event.target.closest('[data-cart-change]');
      if (!trigger) return;
      if (drawer.getAttribute('aria-busy') === 'true') return;
      const status = drawer.querySelector('[data-cart-error]');
      const controls = [...drawer.querySelectorAll('[data-cart-change]')];
      controls.forEach(control => { control.disabled = true; });
      drawer.setAttribute('aria-busy', 'true');
      cartMutationQueue = cartMutationQueue
        .then(() => updateCart(trigger.dataset.cartChange, Number(trigger.dataset.quantity)))
        .then(() => { if (status) status.textContent = ''; const controlsNow = [...drawer.querySelectorAll('[data-cart-change]')]; const replacement = controlsNow.find(node => node.dataset.cartChange === trigger.dataset.cartChange && node.textContent === trigger.textContent) || controlsNow[0] || drawer.querySelector('[data-nube-close]'); replacement?.focus({ preventScroll: true }); })
        .catch(error => { if (status) status.textContent = error.message; })
        .finally(() => { controls.forEach(control => { control.disabled = false; }); drawer.removeAttribute('aria-busy'); });
    }));
  }

function newsletter(scope = document) {
  scope.querySelectorAll('[data-nube-newsletter]').forEach(modal =>
    bindOnce(modal, 'Newsletter', () => {
      const days = Number(
        modal.dataset.frequencyDays || 30
      );

      const delay = Number(
        modal.dataset.delay || 8
      ) * 1000;

      const key = 'nubeNewsletterDismissedAt';
      const scrollKey = 'nubeNewsletterScrollPosition';

      const panel = modal.querySelector(
        '[data-newsletter-panel]'
      );

      const form = modal.closest('form');

      let opener, hideTimer, openTimer;

      const restoreScroll = () => {
        try {
          const saved = JSON.parse(
            sessionStorage.getItem(scrollKey) || 'null'
          );

          if (
            !saved ||
            saved.path !== location.pathname ||
            !Number.isFinite(saved.y) || (saved.at && Date.now() - saved.at > 1800000)
          ) {
            return;
          }

          const previousRestoration = history.scrollRestoration;
          if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

          const restore = () => {
            window.scrollTo({
              top: saved.y,
              left: 0,
              behavior: 'auto'
            });
          };

          restore();

          requestAnimationFrame(() => {
            requestAnimationFrame(restore);
          });

          window.addEventListener(
            'load',
            restore,
            {
              once: true
            }
          );

          if (document.fonts?.ready) {
            document.fonts.ready
              .then(restore)
              .catch(() => {});
          }

          setTimeout(() => {
            restore();
            try { sessionStorage.removeItem(scrollKey); } catch (_) {}
            if (previousRestoration) history.scrollRestoration = previousRestoration;
          }, 700);
        } catch (error) {
          try { sessionStorage.removeItem(scrollKey); } catch (_) {}
        }
      };

      const show = trigger => {
        if (!modal.isConnected) return;
        clearTimeout(hideTimer); clearTimeout(openTimer);
        opener = trigger;
        modal.hidden = false;
        document.querySelectorAll('[data-nube-newsletter-open]').forEach(node => node.setAttribute('aria-expanded', 'true'));

        requestAnimationFrame(() => {
          modal.classList.add('is-open');
        });
        if (trigger) setTimeout(() => panel.querySelector(SELECTOR_FOCUSABLE)?.focus({ preventScroll: true }), 330);
      };

      const hide = () => {
        clearTimeout(hideTimer); clearTimeout(openTimer);
        const scrollPosition = window.scrollY;

        if (modal.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        modal.classList.remove('is-open');
        try { localStorage.setItem(key, Date.now()); } catch (_) {}
        document.querySelectorAll('[data-nube-newsletter-open]').forEach(node => node.setAttribute('aria-expanded', 'false'));

        hideTimer = setTimeout(() => {
          modal.hidden = true;

          opener?.focus?.({
            preventScroll: true
          });

          requestAnimationFrame(() => {
            window.scrollTo({
              top: scrollPosition,
              left: 0,
              behavior: 'auto'
            });
          });
        }, 330);
      };

      document
        .querySelectorAll('[data-nube-newsletter-open]')
        .forEach(trigger =>
          bindOnce(trigger, 'NewsletterOpen', () => {
            trigger.addEventListener('click', event => {
              event.preventDefault();
              show(trigger);
            });
          })
        );

      modal
        .querySelectorAll('[data-newsletter-close]')
        .forEach(button => {
          button.addEventListener('click', hide);
        });

      modal.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          hide();
        }
        trapFocus(panel, event);
      });

      const onDocumentKeydown = event => {
        if (event.key === 'Escape' && !modal.hidden) hide();
      };

      const onDocumentPointerdown = event => {
        const clickedOutside =
          !modal.hidden &&
          !panel.contains(event.target) &&
          !event.target.closest(
            '[data-nube-newsletter-open]'
          );

        if (clickedOutside) {
          hide();
        }
      };
      document.addEventListener('keydown', onDocumentKeydown);
      document.addEventListener('pointerdown', onDocumentPointerdown);

      /*
       * Shopify controla completamente el envío.
       * El script únicamente recuerda la posición.
       */
      if (form) {
        bindOnce(form, 'NewsletterSubmit', () => {
          form.addEventListener(
            'submit',
            () => {
              try { sessionStorage.setItem(
                scrollKey,
                JSON.stringify({
                  path: location.pathname,
                  y: window.scrollY,
                  at: Date.now()
                })
              ); } catch (_) {}
            },
            {
              capture: true
            }
          );
        });
      }

      restoreScroll();

      let dismissed = 0;
      try { dismissed = Number(localStorage.getItem(key) || 0); } catch (_) {}

      if (modal.dataset.success === 'true') {
        try { localStorage.setItem(key, Date.now()); } catch (_) {}

        announce(panel.querySelector('[role="status"]')?.textContent.trim() || 'Suscripción confirmada.');
        const cleanSearch = location.search
          .replace(
            /([?&])(customer_posted|contact_posted)=true(&|$)/,
            '$1'
          )
          .replace(/[?&]$/, '');

        const cleanHash =
          location.hash === '#contact_form' ||
          location.hash === `#${form?.id}`
            ? ''
            : location.hash;

        history.replaceState(
          {},
          '',
          `${location.pathname}${cleanSearch}${cleanHash}`
        );
      } else if (modal.dataset.errors === 'true') {
        show();
      } else if (
        !dismissed ||
        Date.now() - dismissed > days * 86400000
      ) {
        let privacyWasBlocking = false;

        const privacyBannerVisible = () => {
          const banner = document.querySelector('#shopify-pc__banner');
          if (!banner || banner.hidden || banner.getAttribute('aria-hidden') === 'true') return false;
          const style = getComputedStyle(banner);
          return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > 0;
        };

        const tryAutomaticOpen = () => {
          if (document.visibilityState !== 'visible') {
            openTimer = setTimeout(tryAutomaticOpen, 1000);
            return;
          }

          if (privacyBannerVisible()) {
            privacyWasBlocking = true;
            openTimer = setTimeout(tryAutomaticOpen, 1000);
            return;
          }

          if (privacyWasBlocking) {
            privacyWasBlocking = false;
            openTimer = setTimeout(tryAutomaticOpen, delay);
            return;
          }

          if (document.body.classList.contains('nube-drawer-open') || document.querySelector('[role="dialog"]:not([hidden]).is-open')) {
            openTimer = setTimeout(tryAutomaticOpen, 1000);
            return;
          }

          show();
        };

        openTimer = setTimeout(tryAutomaticOpen, delay);
      }

      registerCleanup(modal, () => {
        clearTimeout(hideTimer);
        clearTimeout(openTimer);
        document.removeEventListener('keydown', onDocumentKeydown);
        document.removeEventListener('pointerdown', onDocumentPointerdown);
      });
    })
  );
}
  function processIndex() {
    document.querySelectorAll('[data-nube-process-index]:not([data-auto-sync="false"])').forEach(nav => {
      const steps = [...document.querySelectorAll('[data-nube-process-step]')];
      if (!steps.length) return;
      const links = steps.map((step, index) => {
        const title = step.querySelector('h2')?.textContent.trim();
        if (!step.id) step.id = `nube-process-${step.closest('.shopify-section')?.id || index + 1}`;
        // A duplicated editor section may inherit its anchor; keep the first link compatible.
        if (steps.slice(0, index).some(previous => previous.id === step.id)) step.id += `-${index + 1}`;
        const number = String(index + 1).padStart(2, '0');
        const label = step.querySelector('[data-process-number]');
        if (label) label.textContent = number;
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(step.id)}`;
        const count = document.createElement('span'); count.textContent = number;
        const heading = document.createElement('span'); heading.textContent = title || number;
        link.append(count, heading);
        return link;
      });
      nav.replaceChildren(...links);
      anchorNavigation(nav);
    });
  }
  function init(scope = document) {
    processIndex(); drawers(); header(); scrollExit(); anchorNavigation(scope); reveals(scope); materials(scope); quantity(scope); accordions(scope); productForms(scope); productMagnifier(scope); mobileProductViewer(scope); cart(scope); newsletter(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:reorder', processIndex);
  document.addEventListener('shopify:section:unload', event => {
    cleanupScope(event.target);
    requestAnimationFrame(processIndex);
  });
})();
